import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Stack } from 'react-bootstrap'

import { Button } from '../../../../../components/Button/Button'
import { useExportConfirmation } from '../../../../../components/ConfirmationDialog/useExportConfirmation'
import { DocumentExportType } from '../../../../../components/DocumentConnector/models'
import { SaveToDocumentConnectorButton } from '../../../../../components/DocumentConnector/SaveToDocumentConnectorButton'
import { useExportToPDF } from '../../../../../components/PDFExportButton/hooks'
import { type AIAgentResult, AIAgentType } from '../../../../../models/AIAgent'
import { type MeetingPrepResult } from '../../../../../models/MeetingPrep'
import { type User } from '../../../../../models/User'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { aiAgentResultAnchor } from '../../../../../store/documentConnectors/documentConnectors'
import { useGetExternalUserByIdQuery } from '../../../../../store/externalUser/externalUser'
import { useAppSelector } from '../../../../../store/hooks'
import { selectSessionMeetingTemplate } from '../../../../../store/meetingSummary/meetingTemplates'
import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { AIAgentLoader } from '../../../../../views/AIAgent/AIAgentLoader/AIAgentLoader'
import { AIAgentResultCitations, type Citation } from '../../../../../views/AIAgent/AIAgentResultCitations/AIAgentResultCitations'
import { useAIAgentResultCopy } from '../../../../../views/AIAgent/hooks/useAIAgentResultCopy'
import { useAIAgentResultState } from '../../../../../views/AIAgent/hooks/useAIAgentResultState'
import { formatDateDDMMYYYY, formatDateHM } from '../../common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../common/tracking'
import { useMeetingUsers } from '../../MeetingPreparation/hooks/users'
import { buildMeetingPrepFileName, PREP_PDF_STYLES } from '../../MeetingPreparation/meetingPrepExport'
import { MeetingPrepSummary } from '../../MeetingPreparation/Prep/MeetingPrepSummary'
import { getRoomTrackingDetails } from '../../tracking'
import { getCitationNodesFromEditorState } from '../MeetingResults/Lexical/Nodes/CitationNode/CitationNode'
import { parseMetadata } from '../utils'
import { MeetingPrepEmptyState } from './MeetingPrepEmptyState'

interface MeetingPrepErrorProps {
  onRetry?: () => void
}

const MeetingPrepError: React.FC<MeetingPrepErrorProps> = ({ onRetry }) => (
  <Stack gap={3} className='align-items-center py-5'>
    <div className='paragraph-medium zocks-text-gray-600'>The meeting prep could not be loaded.</div>
    {onRetry ? (
      <Button variant='header' onClick={onRetry}>
        <Stack direction='horizontal' gap={2}>
          <i className='bi bi-arrow-clockwise' />
          <span>Retry</span>
        </Stack>
      </Button>
    ) : null}
  </Stack>
)

interface MeetingPrepProps {
  prep: AIAgentResult | null | undefined
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

export const MeetingPrep: React.FC<MeetingPrepProps> = ({ prep, isLoading, isError, onRetry }) => {
  const accountId = useAppSelector(selectAccountId)
  const { trackingClient, tracked } = useTracking()

  const roomSession = useAppSelector(selectRoomSession)
  const meetingTemplate = useAppSelector(selectSessionMeetingTemplate)

  const meetingPrep = (prep?.result ?? null) as MeetingPrepResult | null
  const aiAgentResultId = prep?.id
  const citations = useMemo<Citation[] | null>(() => parseMetadata(prep?.metadata).citations ?? null, [prep?.metadata])
  const documentState = useAIAgentResultState(meetingPrep?.document)

  const filteredCitations = useMemo(() => {
    if (!citations?.length) {
      return []
    }
    const citationIds = new Set(getCitationNodesFromEditorState(documentState.editorState).map((node) => node.getCitationId()))
    return citations.filter((citation) => citationIds.has(citation.id))
  }, [citations, documentState])

  const { usersByRoles } = useMeetingUsers(roomSession, ['external'])
  const clients = usersByRoles['Clients'] ?? []
  const soleContactId = clients.length === 1 ? clients[0].user.id : null
  const { data: soleContactData } = useGetExternalUserByIdQuery({ accountId, userId: soleContactId ?? '' }, { skip: !accountId || !soleContactId })
  const [updatedContact, setUpdatedContact] = useState<User | null>(null)
  const linkableContact = updatedContact ?? soleContactData?.data ?? null

  const summaryRef = useRef(null)
  const { exportToPDF, generatePDFBlob } = useExportToPDF(summaryRef, PREP_PDF_STYLES)
  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useExportConfirmation({ roomSession, exportType: 'document' })
  const { handleCopy } = useAIAgentResultCopy({ aiAgentResultState: documentState })

  const { external } = parseMetadata(roomSession?.metadata)
  const title: string | undefined = external?.title ?? meetingTemplate?.name
  const fileName = buildMeetingPrepFileName('Meeting Prep Summary', roomSession?.startedAt, external?.title, meetingTemplate?.name)
  const trackingDetails = { ...getRoomTrackingDetails(roomSession), aiAgentResultId }

  const roomId = roomSession?.roomId
  const sessionId = roomSession?.id

  useEffect(() => {
    if (!roomId || !sessionId || !aiAgentResultId) {
      return
    }
    trackingClient.track({
      eventName: TrackEvent.MEETING_PREP_ARCHIVE_VIEWED,
      eventDetails: { roomId, sessionId, aiAgentResultId },
    })
  }, [trackingClient, aiAgentResultId, roomId, sessionId])

  const handleCopyClick = tracked(handleCopy, { eventName: TrackEvent.MEETING_PREP_ARCHIVE_COPIED, eventDetails: trackingDetails })
  const handleExportClick = () =>
    runMutationWithConfirmation(
      tracked(() => exportToPDF(fileName), { eventName: TrackEvent.MEETING_PREP_ARCHIVE_PDF_EXPORTED, eventDetails: trackingDetails }),
    )
  const handleSaved = tracked(() => undefined, { eventName: TrackEvent.MEETING_PREP_ARCHIVE_SAVED_TO_INTEGRATION, eventDetails: trackingDetails })

  if (isError) {
    return <MeetingPrepError onRetry={onRetry} />
  }

  if (isLoading || !roomSession) {
    return <AIAgentLoader withChat={false} />
  }

  if (!prep) {
    return <MeetingPrepEmptyState />
  }

  if (!meetingPrep?.document) {
    return <MeetingPrepError />
  }

  return (
    <Stack gap={4} className='mt-3'>
      <ConfirmationModal />
      <Stack direction='horizontal' gap={2} className='flex-wrap'>
        <SaveToDocumentConnectorButton
          anchor={aiAgentResultAnchor(prep.id)}
          variants={[{ label: 'Summary', generatePDFBlob, defaultFileName: fileName }]}
          contact={linkableContact}
          onContactUpdated={setUpdatedContact}
          onSaved={handleSaved}
          roomSession={roomSession}
          exportType={DocumentExportType.MEETING_PREP}
        />
        <Button variant='header' onClick={handleCopyClick}>
          <Stack direction='horizontal' gap={2}>
            <i className='bi bi-copy' />
            <span>Copy</span>
          </Stack>
        </Button>
        <Button variant='header' onClick={handleExportClick} disabled={accountIsLoading}>
          <Stack direction='horizontal' gap={2}>
            <i className='bi bi-file-pdf' />
            <span>Export to PDF</span>
          </Stack>
        </Button>
      </Stack>
      <div className='d-flex p-3 gap-3 align-items-center zocks-border-0.5 zocks-bg-color-gray-100 border border-1 zocks-border-color-gray-300 rounded-3'>
        <i className='bi bi-info-square fs-6' />
        <div className='paragraph-medium'>
          Last updated on {formatDateDDMMYYYY(prep.updatedAt)} at {formatDateHM(prep.updatedAt)}. Read-only.
        </div>
      </div>
      <div ref={summaryRef} className='d-flex flex-column gap-3 prep-container'>
        {title ? <h3 className='mb-0'>{title}</h3> : null}
        <MeetingPrepSummary readOnly documentState={documentState} citations={filteredCitations} />
      </div>
      <AIAgentResultCitations aiAgentType={AIAgentType.MEETING_PREPARATION} citationIsLoading={false} citations={filteredCitations} />
    </Stack>
  )
}
